import { type FormSchema, useFormModel } from '@fv/client-core'
import type {
  DeepPartial,
  HazardousDetailFormModel,
  LegacyReadOnlyDetail,
} from '@fv/client-types'

import { InputGroup, InputGroupWrapper } from '@/components/inputs/InputGroup'
import YesNoToggle from '@/components/inputs/YesNoToggle'
import {
  useHazardClassOptions,
  usePackingGroupOptions,
} from '@/hooks/settings/useEnumOptions'

import { validateHazmatId } from '../commodityUtils'
import { CfrHazmatTypeahead } from './CfrHazmatTypeahead'
import {
  mapCfrMaterialToHazmatFields,
  useRequirePrimaryClass,
} from './cfrHazmat'
import { useItemsState } from './loadItemsContext'
import { TechnicalNameField } from './TechnicalNameField'

const schema: FormSchema<HazardousDetailFormModel> = {
  hazmatId: {
    validate: validateHazmatId,
  },
  reportableQuantity: { valueAsChecked: true },
  residueLastContained: { valueAsChecked: true },
  limitedQuantity: { valueAsChecked: true },
  marinePollutant: { valueAsChecked: true },
}

type Props = {
  value?: HazardousDetailFormModel
  readOnly?: DeepPartial<LegacyReadOnlyDetail>
  onChange: (value: Partial<HazardousDetailFormModel>) => void
  itemId: string
  disabled?: boolean
}
export const HazmatForm = ({
  disabled,
  itemId,
  value,
  readOnly,
  onChange,
}: Props) => {
  const { isTemplate } = useItemsState()
  const required = !isTemplate
  const packingGroupOptions = usePackingGroupOptions()
  const { hazardClassOptions, hazardSubClassOptions } = useHazardClassOptions()
  const { register } = useFormModel({
    value: value ?? {},
    key: `${itemId}.hazard`,
    onChange,
    schema,
    disabled,
  })
  const properShippingName = register('properShippingName')
  const hazmatId = register('hazmatId')

  const requiresPrimaryClass = useRequirePrimaryClass(value?.properShippingName)

  return (
    <>
      <InputGroup
        className="col-span-3"
        inputProps={properShippingName}
        inputType="custom"
        label="Proper shipping name"
        locked={readOnly?.properShippingName}
        required={required}
      >
        <CfrHazmatTypeahead
          field="properShippingName"
          name={properShippingName.name}
          value={(properShippingName.value as string) ?? ''}
          readOnly={readOnly?.properShippingName}
          disabled={properShippingName.disabled}
          required={required}
          onChange={val =>
            onChange({ ...(value ?? {}), properShippingName: val })
          }
          onSelectMaterial={material =>
            onChange({
              ...(value ?? {}),
              ...mapCfrMaterialToHazmatFields(material),
            })
          }
        />
      </InputGroup>
      <InputGroup
        inputProps={hazmatId}
        inputType="custom"
        label="Hazmat ID"
        locked={readOnly?.hazmatId}
        required={required}
      >
        <CfrHazmatTypeahead
          field="identificationNumber"
          name={hazmatId.name}
          value={(hazmatId.value as string) ?? ''}
          readOnly={readOnly?.hazmatId}
          disabled={hazmatId.disabled}
          required={required}
          onChange={val => onChange({ ...(value ?? {}), hazmatId: val })}
          onSelectMaterial={material =>
            onChange({
              ...(value ?? {}),
              ...mapCfrMaterialToHazmatFields(material),
            })
          }
        />
      </InputGroup>

      <InputGroup
        inputProps={{
          ...register('primaryClass'),
          options: hazardClassOptions,
        }}
        inputType="select"
        label="Hazard class"
        locked={readOnly?.hazardClass}
        required={required && requiresPrimaryClass}
      />

      <InputGroup
        inputProps={{
          ...register('packingGroup'),
          options: packingGroupOptions,
        }}
        inputType="select"
        label="Packing group"
        locked={readOnly?.packingGroup}
      />

      <InputGroup
        inputProps={{
          ...register('secondaryClass'),
          options: hazardSubClassOptions,
        }}
        inputType="select"
        label="Sub hazard class 1"
        locked={readOnly?.secondaryHazardClass}
      />

      <InputGroup
        inputProps={{
          ...register('tertiaryClass'),
          options: hazardSubClassOptions,
        }}
        inputType="select"
        label="Sub hazard class 2"
        locked={readOnly?.tertiaryHazardClass}
      />

      <InputGroup
        inputProps={register('permitNumber')}
        inputType="text"
        label="Special permit #"
        locked={readOnly?.specialPermit}
      />

      <TechnicalNameField
        className="col-span-3"
        inputProps={register('technicalName')}
        locked={readOnly?.technicalName}
        properShippingName={value?.properShippingName}
      />

      <div className="col-span-full grid grid-cols-4 gap-x-4 gap-y-2 py-2 b750:grid-cols-2">
        <InputGroupWrapper
          name="reportableQuantity"
          label="Reportable quantity"
        >
          <YesNoToggle {...register('reportableQuantity')} />
        </InputGroupWrapper>
        <InputGroupWrapper
          name="residueLastContained"
          label="Residue last contained"
        >
          <YesNoToggle {...register('residueLastContained')} />
        </InputGroupWrapper>
        <InputGroupWrapper name="limitedQuantity" label="Limited quantity">
          <YesNoToggle {...register('limitedQuantity')} />
        </InputGroupWrapper>
        <InputGroupWrapper name="marinePollutant" label="Marine pollutant">
          <YesNoToggle {...register('marinePollutant')} />
        </InputGroupWrapper>
      </div>
    </>
  )
}
